import React, { useEffect, useState } from 'react';

import { Checkbox, Popconfirm, Radio } from 'antd';
import {
  COPY_TOOL_FILTER_EMPTY_MESSAGE,
  COPY_TOOL_FILTER_NO_SOURCE_MESSAGE,
  COPY_TOOL_FILTER_UNCATEGORISED_MESSAGE,
  COPY_TOOL_MATCH_RULE_CHANGE_DESCRIPTION,
  COPY_TOOL_MATCH_RULE_CHANGE_TITLE,
} from 'constants/messageConstants';
import {
  COPY_TOOL_CANCEL_LABEL,
  COPY_TOOL_CATEGORIES_LABEL,
  COPY_TOOL_CATEGORY_COUNT_LABEL,
  COPY_TOOL_CATEGORY_CROP_AND_VARIETY_LABEL,
  COPY_TOOL_CATEGORY_CROP_LABEL,
  COPY_TOOL_CATEGORY_LABELS,
  COPY_TOOL_DATA_FILTER_LABEL,
  COPY_TOOL_MATCH_RULE_CHANGE_CONFIRM_LABEL,
  COPY_TOOL_MATCH_RULE_CONTAINS_HINT,
  COPY_TOOL_MATCH_RULE_CONTAINS_LABEL,
  COPY_TOOL_MATCH_RULE_EXACT_HINT,
  COPY_TOOL_MATCH_RULE_EXACT_LABEL,
  COPY_TOOL_MATCH_RULE_FULL_HINT,
  COPY_TOOL_MATCH_RULE_LABEL,
} from 'constants/uiConstants';
import { CopyDataCategory, CopyMatchRule } from 'interfaces/ICopyPaddockData';

import {
  COPY_DATA_CATEGORY_ORDER,
  CopyDataFilterContext,
  CopyDataFilterState,
} from './copyDataFilter';

const NO_CATEGORY_COUNTS: ReadonlyMap<CopyDataCategory, number> = new Map();

const MATCH_RULE_OPTIONS = [
  { label: COPY_TOOL_MATCH_RULE_CONTAINS_LABEL, value: CopyMatchRule.Contains },
  { label: COPY_TOOL_MATCH_RULE_EXACT_LABEL, value: CopyMatchRule.Exact },
];

const MATCH_RULE_HINTS: Record<CopyMatchRule, string> = {
  [CopyMatchRule.Contains]: COPY_TOOL_MATCH_RULE_CONTAINS_HINT,
  [CopyMatchRule.Exact]: COPY_TOOL_MATCH_RULE_EXACT_HINT,
};

// Seed and Seed Dressings sit under Crop, beside the document's Crop & Variety.
const CROP_CATEGORIES = [CopyDataCategory.Seed, CopyDataCategory.SeedDressing];
const OTHER_CATEGORIES = COPY_DATA_CATEGORY_ORDER.filter(
  (category) => !CROP_CATEGORIES.includes(category)
);

interface CopyDataFilterControlsProps {
  /** Null until a Source document is ticked. */
  filter: CopyDataFilterState | null;
  context: CopyDataFilterContext | null;
  isFullCopy: boolean;
  /** Source's Plan or Actual label, for the empty message. */
  modeLabel: string;
  onMatchRuleChange: (matchRule: CopyMatchRule) => void;
  onCategoriesChange: (
    categories: CopyDataCategory[],
    includeCropAndVariety: boolean
  ) => void;
}

/** The Contains / Exact match rule and the category ticks for the Source document. */
const CopyDataFilterControls: React.FC<CopyDataFilterControlsProps> = ({
  filter,
  context,
  isFullCopy,
  modeLabel,
  onMatchRuleChange,
  onCategoriesChange,
}) => {
  const counts = context?.categoryCounts ?? NO_CATEGORY_COUNTS;
  // A rule change waiting on the warning, as it would undo manual task ticks; dropped for another Source or mode.
  const [pendingChange, setPendingChange] = useState<{
    sourceKey: string;
    matchRule: CopyMatchRule;
  } | null>(null);
  // Hidden at once, and cleared below, once the manual ticks it protects are gone.
  const pendingMatchRule =
    filter?.hasManualTaskChanges &&
    pendingChange?.sourceKey === filter.sourceKey
      ? pendingChange.matchRule
      : null;
  useEffect(() => {
    setPendingChange(null);
  }, [filter?.sourceKey, filter?.hasManualTaskChanges]);
  const setPendingMatchRule = (matchRule: CopyMatchRule | null) =>
    setPendingChange(
      filter && matchRule !== null
        ? { sourceKey: filter.sourceKey, matchRule }
        : null
    );

  const handleMatchRuleChange = (matchRule: CopyMatchRule) => {
    if (filter?.hasManualTaskChanges) setPendingMatchRule(matchRule);
    else onMatchRuleChange(matchRule);
  };

  // The rule's hint sits on the title line once there's something to filter.
  const hasFilterOptions =
    !!filter && !!context && (counts.size > 0 || context.hasCropAndVariety);

  const renderBody = () => {
    if (!filter || !context) {
      return (
        <span className="text-xs text-gray-muted">
          {COPY_TOOL_FILTER_NO_SOURCE_MESSAGE}
        </span>
      );
    }
    if (counts.size === 0 && !context.hasCropAndVariety) {
      // Tasks with no categorised step can only be copied whole; the task list still shows them.
      return (
        <span className="text-xs text-gray-muted">
          {context.tasks.length === 0
            ? COPY_TOOL_FILTER_EMPTY_MESSAGE(modeLabel)
            : COPY_TOOL_FILTER_UNCATEGORISED_MESSAGE(modeLabel)}
        </span>
      );
    }

    const ticked = new Set(filter.categories);
    const setCategories = (
      categories: CopyDataCategory[],
      checked: boolean,
      includeCropAndVariety = filter.includeCropAndVariety
    ) => {
      const next = new Set(ticked);
      categories.forEach((category) =>
        checked ? next.add(category) : next.delete(category)
      );
      onCategoriesChange(
        COPY_DATA_CATEGORY_ORDER.filter((category) => next.has(category)),
        includeCropAndVariety
      );
    };

    const cropCategories = CROP_CATEGORIES.filter((c) => counts.has(c));
    const cropTicks = [
      ...(context.hasCropAndVariety ? [filter.includeCropAndVariety] : []),
      ...cropCategories.map((category) => ticked.has(category)),
    ];
    const isCropChecked = cropTicks.every(Boolean);
    const isCropIndeterminate = !isCropChecked && cropTicks.some(Boolean);

    const renderCategory = (category: CopyDataCategory) => (
      <Checkbox
        key={category}
        checked={ticked.has(category)}
        onChange={(event) => setCategories([category], event.target.checked)}
      >
        {COPY_TOOL_CATEGORY_COUNT_LABEL(
          COPY_TOOL_CATEGORY_LABELS[category],
          counts.get(category) ?? 0
        )}
      </Checkbox>
    );

    return (
      <>
        <span className="w-fit">
          <Radio.Group
            aria-label={COPY_TOOL_MATCH_RULE_LABEL}
            value={filter.matchRule}
            disabled={isFullCopy}
            onChange={(event) =>
              handleMatchRuleChange(event.target.value as CopyMatchRule)
            }
            options={MATCH_RULE_OPTIONS}
          />
        </span>
        <div
          role="group"
          aria-label={COPY_TOOL_CATEGORIES_LABEL}
          className="flex flex-col gap-1"
        >
          {cropTicks.length > 0 && (
            <div className="flex flex-col gap-1">
              <Checkbox
                checked={isCropChecked}
                indeterminate={isCropIndeterminate}
                onChange={(event) =>
                  setCategories(
                    cropCategories,
                    event.target.checked,
                    event.target.checked
                  )
                }
              >
                {COPY_TOOL_CATEGORY_CROP_LABEL}
              </Checkbox>
              <div className="ml-6 flex flex-wrap justify-start gap-x-4 gap-y-1">
                {context.hasCropAndVariety && (
                  <Checkbox
                    checked={filter.includeCropAndVariety}
                    onChange={(event) =>
                      setCategories([], true, event.target.checked)
                    }
                  >
                    {COPY_TOOL_CATEGORY_CROP_AND_VARIETY_LABEL}
                  </Checkbox>
                )}
                {cropCategories.map(renderCategory)}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {OTHER_CATEGORIES.filter((c) => counts.has(c)).map(renderCategory)}
          </div>
        </div>
      </>
    );
  };

  return (
    <fieldset className="flex min-w-0 flex-1 flex-col gap-2 md:max-w-[32rem]">
      <legend className="mb-1 text-sm font-semibold text-charcoal">
        {COPY_TOOL_DATA_FILTER_LABEL}
        {hasFilterOptions && filter && (
          <>
            {' '}
            {/* Anchored on the hint, not the radio: a radio click is an outside click, which closes it on mousedown before the change sets the new pick. */}
            <Popconfirm
              open={pendingMatchRule !== null}
              title={COPY_TOOL_MATCH_RULE_CHANGE_TITLE}
              description={COPY_TOOL_MATCH_RULE_CHANGE_DESCRIPTION}
              okText={COPY_TOOL_MATCH_RULE_CHANGE_CONFIRM_LABEL}
              cancelText={COPY_TOOL_CANCEL_LABEL}
              placement="bottomLeft"
              onConfirm={() => {
                if (pendingMatchRule !== null)
                  onMatchRuleChange(pendingMatchRule);
                setPendingMatchRule(null);
              }}
              // Opened only by a rule pick; Cancel, Escape and outside clicks drop the pick.
              onOpenChange={(open) => !open && setPendingMatchRule(null)}
            >
              <span className="pl-2 text-xs font-normal text-gray-muted">
                {isFullCopy
                  ? COPY_TOOL_MATCH_RULE_FULL_HINT
                  : MATCH_RULE_HINTS[filter.matchRule]}
              </span>
            </Popconfirm>
          </>
        )}
      </legend>
      {renderBody()}
    </fieldset>
  );
};

export default CopyDataFilterControls;
